import { FHIRIdentifierCopier } from "@/components/DebugTools/FHIRIdentifierCopier";
import { AlertDialog, BodyText, Collapsible, toast } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { capitalize } from "@/lib/formatters";
import { formatDateTime } from "@/lib/time";
import { trackEvent } from "@/lib/tracking";
import { constants, getCoding } from "@curai/js-fhir-tools";
import { useParams } from "@tanstack/react-router";
import {
  Check,
  CirclePlay,
  CircleX,
  ClipboardPlus,
  LoaderCircle,
  Octagon,
  SquareCheck,
} from "lucide-react";
import { useState } from "react";
import {
  Action,
  AllergyStatus,
  type PatientAllergy,
  useAllergyLastUpdateAgent,
  useConfirmAllergy,
  useMarkAllergyStatus,
} from "../../network/useAllergies";
import { UpdateAllergyForm } from "./UpdateAllergyForm";

interface AllergyEntryProps {
  allergy: PatientAllergy;
  status: AllergyStatus;
  isBatchConfirmingAllergies: boolean;
}

export const AllergyEntry = ({
  allergy,
  status,
  isBatchConfirmingAllergies,
}: AllergyEntryProps) => {
  const { allergyIntolerance } = allergy;
  const { patientId } = useParams({ from: "/patient/$patientId" });

  const isNoKnownDrugAllergy =
    allergyIntolerance.code != null &&
    getCoding(allergyIntolerance.code, constants.SNOMED_SYSTEM_URL)?.code ===
      "409137002";
  const [showVoidPrompt, setShowVoidPrompt] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);

  const { data: lastUpdateAgent } = useAllergyLastUpdateAgent({
    patientId,
    allergyIntoleranceId: allergyIntolerance.id as string,
    enabled: hasOpened,
  });

  const {
    isSavingStatus,
    markAllergyActive,
    markAllergyInactive,
    markAllergyResolved,
    markAllergyVoided,
  } = useMarkAllergyStatus({
    patientId,
    allergyIntoleranceId: allergyIntolerance.id as string,
    onError: ({ message }: { message: string }) => {
      toast.error(message);
    },
  });

  const { isPending: isConfirming, mutate: confirmAllergy } = useConfirmAllergy(
    {
      patientId,
      allergyIntoleranceId: allergyIntolerance.id as string,
    },
  );

  const actionMenuItems = [];
  if (
    !isConfirming &&
    !isBatchConfirmingAllergies &&
    allergy.needsConfirmation &&
    allergy.availableConfirmationActions.find(
      (action) => action.action === Action.CONFIRM && !action.unavailableReason,
    )
  ) {
    actionMenuItems.push({
      label: "Confirm",
      icon: <Check className="stroke-error" size={16} />,
      onClick: confirmAllergy,
    });
  }

  if (isSavingStatus || isConfirming) {
    actionMenuItems.push({
      label: "Saving...",
      icon: <LoaderCircle size={16} className="animate-spin" />,
      onClick: () => {},
    });
  }

  const forcedDropdownActions = [
    {
      label: "Active",
      icon: <CirclePlay className="mr-2" size={16} />,
      onClick: () => {
        trackEvent(TRACKERS.MARK_ALLERGY_AS_ACTIVE);
        markAllergyActive();
      },
      disabled: Boolean(
        allergy.availableStatusChangeActions?.find(
          (action) =>
            action.action === Action.ACTIVE && !!action.unavailableReason,
        ),
      ),
    },
    {
      label: "Inactive",
      icon: <Octagon className="mr-2" size={16} />,
      onClick: () => {
        trackEvent(TRACKERS.MARK_INACTIVE_ON_ALLERGY);
        markAllergyInactive();
      },
      disabled: Boolean(
        allergy.availableStatusChangeActions?.find(
          (action) =>
            action.action === Action.INACTIVE && !!action.unavailableReason,
        ),
      ),
    },
    {
      label: "Resolved",
      icon: <SquareCheck className="mr-2" size={16} />,
      onClick: () => {
        trackEvent(TRACKERS.RESOLVE_ALLERGY);
        markAllergyResolved();
      },
      disabled: Boolean(
        allergy.availableStatusChangeActions?.find(
          (action) =>
            action.action === Action.RESOLVED && !!action.unavailableReason,
        ),
      ),
    },
    {
      label: "Void",
      icon: <CircleX className="mr-2" size={16} />,
      onClick: () => {
        trackEvent(TRACKERS.VOID_ALLERGY);
        setShowVoidPrompt(true);
      },
      disabled: Boolean(
        allergy.availableStatusChangeActions?.find(
          (action) =>
            action.action === Action.VOID && !!action.unavailableReason,
        ),
      ),
    },
  ];

  return (
    <>
      <Collapsible
        title={
          isNoKnownDrugAllergy
            ? allergyIntolerance.allergyName
            : `${allergyIntolerance.allergyName} (${capitalize(allergyIntolerance.allergyType)})`
        }
        headerSize="sm"
        actionMenu={actionMenuItems}
        background="danger"
        headerTextColor="error"
        status={
          status === AllergyStatus.INACTIVE
            ? capitalize(allergyIntolerance.clinicalStatusCode)
            : undefined
        }
        forcedDropdownActions={!isSavingStatus ? forcedDropdownActions : []}
        showActionTooltips={true}
        titleAction={
          <FHIRIdentifierCopier id={allergyIntolerance.id as string} />
        }
        onOpenChange={(open) => {
          if (open) setHasOpened(true);
        }}
      >
        <UpdateAllergyForm
          allergyIntolerance={allergyIntolerance}
          key={`${allergyIntolerance.id}-${allergyIntolerance.meta?.versionId}`}
          hideAllergyType={isNoKnownDrugAllergy}
        />
        {allergyIntolerance.recorder?.display && (
          <div className="flex flex-row gap-1 items-center mt-2">
            <ClipboardPlus size={16} className="stroke-tertiary" />
            <BodyText size="xs" textColor="tertiary">
              Added by {allergyIntolerance.recorder.display} on{" "}
              {formatDateTime(allergyIntolerance.recordedDate)}
            </BodyText>
          </div>
        )}
        {lastUpdateAgent?.agentDisplay && (
          <div className="flex flex-row gap-1 items-center mt-2">
            <ClipboardPlus size={16} className="stroke-tertiary" />
            <BodyText size="xs" textColor="tertiary">
              Last updated by {lastUpdateAgent.agentDisplay} on{" "}
              {formatDateTime(lastUpdateAgent.lastUpdatedAt)}
            </BodyText>
          </div>
        )}
      </Collapsible>
      <AlertDialog
        isOpen={showVoidPrompt}
        onOpenChange={setShowVoidPrompt}
        isControlled
        title="Void Allergy"
        body={`Are you sure you want to void this allergy? This will remove it entirely from the patient's medical record, and should only be used if was added in error (i.e. the wrong patient).`}
        actionLabel="Void"
        cancelLabel="Cancel"
        onAction={() => {
          setShowVoidPrompt(false);
          markAllergyVoided();
        }}
        onCancel={() => {
          setShowVoidPrompt(false);
        }}
        // TODO: Remove this once we fix the AlertDialogProvider
        // biome-ignore lint/complexity/noUselessFragments: We need to pass a trigger element to the AlertDialog so the AlertDialogProvider doesn't render the default trigger element
        triggerElement={<></>}
      />
    </>
  );
};
